<template>
  <div class="et-page">
    <q-linear-progress
      v-if="isFetching"
      indeterminate
      color="primary"
      class="et-page__progress"
    />

    <div class="et-page__header">
      <h1 class="et-page__title">Мои задачи</h1>
      <div class="et-page__header-actions">
        <q-btn
          flat
          round
          icon="refresh"
          class="et-refresh-btn"
          :loading="isFetching"
          @click="refetch"
        >
          <q-tooltip>Обновить список</q-tooltip>
        </q-btn>
      </div>
    </div>

    <div class="et-controls-band">
      <div class="et-filters-row">
        <div class="et-filters">
          <q-select
            v-model="executionFilter"
            :options="employeeTaskExecutionFilterOptions"
            label="Статус выполнения"
            dense
            outlined
            emit-value
            map-options
            class="cd-filter cd-filter--md"
            @update:model-value="onExecutionFilterChange"
          />

          <q-input
            v-model="searchQuery"
            dense
            outlined
            clearable
            debounce="300"
            label="Поиск"
            class="cd-filter cd-filter--search"
          >
            <template #prepend>
              <q-icon name="search" class="et-search-icon" />
            </template>
          </q-input>
        </div>
      </div>
    </div>

    <div class="et-table-card">
      <CustomTable
        class="et-table"
        :rows-data="displayRows"
        :columns="employeeTasksColumns"
        :loading="isFetching"
        :show-search="false"
        :custom-body="true"
        row-key="employee_task_id"
        :sticky-header="true"
        :height="200"
        :hoverless="true"
        :no-data-label="emptyStateLabel"
      >
        <template #customBody="props">
          <q-tr
            :props="props"
            :class="[
              'et-table__row',
              props.row.is_done ? 'et-table__row--done' : 'et-table__row--open',
            ]"
          >
            <custom-table-cell align="center" vertical-align="center">
              <span class="et-cell__id">{{ props.row.employee_task_id || "—" }}</span>
            </custom-table-cell>

            <custom-table-cell align="left" vertical-align="center">
              <div class="et-cell__task">
                <span class="et-cell__task-name">{{ props.row.task_type_name || "—" }}</span>
                <span v-if="props.row.task_type_code" class="et-cell__task-code">{{ props.row.task_type_code }}</span>
              </div>
            </custom-table-cell>

            <custom-table-cell align="left" vertical-align="center">
              <span class="et-cell__main">{{ props.row.resident_name || "—" }}</span>
            </custom-table-cell>

            <custom-table-cell align="left" vertical-align="center">
              <span class="et-cell__secondary">{{ props.row.project_type_name || "—" }}</span>
            </custom-table-cell>

            <custom-table-cell align="left" vertical-align="center">
              <div class="et-cell__meta">
                <span class="et-cell__meta-item">
                  <span class="et-cell__meta-label">Подъезд:</span>
                  {{ props.row.entrance ?? "—" }}
                </span>
                <span class="et-cell__meta-item">
                  <span class="et-cell__meta-label">Этаж:</span>
                  {{ props.row.level_num ?? "—" }}
                </span>
              </div>
            </custom-table-cell>

            <custom-table-cell align="left" vertical-align="center">
              <span class="et-cell__secondary">{{ props.row.work_set_check_group_name || "—" }}</span>
            </custom-table-cell>

            <custom-table-cell align="left" vertical-align="center">
              <span class="et-cell__tag">{{ props.row.placement_type_name || "—" }}</span>
            </custom-table-cell>

            <custom-table-cell align="center" vertical-align="center">
              <span class="et-cell__date">{{ formatTaskRowversion(props.row.rowversion) }}</span>
            </custom-table-cell>

            <custom-table-cell align="center" vertical-align="center">
              <div class="et-status">
                <span
                  :class="[
                    'et-status__badge',
                    props.row.is_done ? 'et-status__badge--done' : 'et-status__badge--open',
                  ]"
                >
                  {{ props.row.is_done ? "Выполнена" : "Не выполнена" }}
                </span>

                <CustomCheckbox
                  v-if="!props.row.is_done && (props.row.task_type_code === 'upload_laboratory_act' || props.row.task_type_code === 'upload_certificate')"
                  :value="false"
                  label="Выполнено"
                  :disable="updatingIsDoneId === props.row.employee_task_id"
                  @change="(v) => onIsDoneCheckboxChange(props.row, v)"
                />
              </div>
            </custom-table-cell>

            <custom-table-cell align="center" vertical-align="center">
              <q-btn
                v-if="!props.row.is_done"
                color="primary"
                dense
                unelevated
                no-caps
                class="et-action-btn"
                label="Выполнить"
                :loading="completingTaskId === props.row.employee_task_id"
                @click="onCompleteClick(props.row)"
              />
              <span v-else class="et-action-done">—</span>
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import CustomTable from "@/components/common/CustomTable";
import { filteredRowsData } from "@/components/common/CustomTable/services";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomCheckbox from "@/components/form/CustomCheckbox/index.vue";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { MODAL_NAMES } from "@/constants";
import { fetchMenuStatuses } from "@/components/layout/Navbar/services";
import { useAppStore } from "@/services/store/app";
import { getFloorWorkSets } from "@/views/ResidentialSettings/services";
import {
  employeeTasksColumns,
  employeeTaskExecutionFilterOptions,
  getEmployeeTasks,
  setEmployeeTaskIsDone,
  formatTaskRowversion,
  tryOpenEmployeeTaskComplete,
  pickWorkSetsFromFloorInfo,
} from "./services";

const { showErrorSnackbar, showSucceedSnackbar } = useCustomSnackbar();
const appStore = useAppStore();
const { showModal, setMenuStatusBadges } = appStore;

const completingTaskId = ref(null);
const updatingIsDoneId = ref(null);
const executionFilter = ref("open");
const searchQuery = ref("");

const { data, isFetching, refetch } = useQuery({
  queryKey: computed(() => ["employeeTasks", executionFilter.value]),
  queryFn: async () => {
    void fetchMenuStatuses().then((menuStatuses) => {
      if (menuStatuses) setMenuStatusBadges(menuStatuses);
    });
    const rows = await getEmployeeTasks(executionFilter.value);
    return rows ?? [];
  },
  refetchOnWindowFocus: false,
});

function onExecutionFilterChange(value) {
  executionFilter.value = value;
}

const rowsData = computed(() => data.value ?? []);

const displayRows = computed(() => {
  const q = (searchQuery.value || "").trim();
  if (!q) return rowsData.value;
  return filteredRowsData(employeeTasksColumns, rowsData.value, q) ?? [];
});

const emptyStateLabel = computed(() => {
  const q = (searchQuery.value || "").trim();
  if (q) return "Ничего не найдено";
  if (executionFilter.value === "open") return "Нет задач для выполнения";
  if (executionFilter.value === "done") return "Нет выполненных задач";
  return "Нет задач";
});

async function onIsDoneCheckboxChange(row, value) {
  if (!value || row.is_done) return;
  if (!row.employee_task_id) return;
  updatingIsDoneId.value = row.employee_task_id;
  const res = await setEmployeeTaskIsDone(row.employee_task_id, true);
  updatingIsDoneId.value = null;
  if (!res) {
    showErrorSnackbar("Не удалось сохранить статус задачи");
    return;
  }
  showSucceedSnackbar();
  refetch();
}

function openCreateAsrModal(row) {
  const floorMapId = row.floor_map_id;
  const placementTypeId = row.placement_type_id;
  const workSetCheckGroupId = row.work_set_check_group_id;
  if (!floorMapId || placementTypeId == null || workSetCheckGroupId == null) {
    showErrorSnackbar("Не хватает данных для создания АСР");
    return;
  }
  showModal(MODAL_NAMES.AsrHiddenWorksPreCall, {
    title: "Акт скрытых работ",
    confirmUnsavedClose: true,
    floorMapId,
    placementTypeId,
    workSetCheckGroupId,
    documentParams: {},
    onSuccess: () => refetch(),
  });
}

async function openCreateExecSchemaModal(row) {
  const floorMapId = row.floor_map_id;
  const placementTypeId = row.placement_type_id;
  const workSetCheckGroupId = row.work_set_check_group_id;
  const projectId = row.project_id;
  if (!floorMapId || placementTypeId == null || workSetCheckGroupId == null || !projectId) {
    showErrorSnackbar("Не хватает данных для создания исполнительной схемы");
    return;
  }
  completingTaskId.value = row.employee_task_id;
  const info = await getFloorWorkSets(floorMapId);
  completingTaskId.value = null;
  if (!info) {
    showErrorSnackbar("Не удалось загрузить работы по этажу");
    return;
  }
  const work_sets = pickWorkSetsFromFloorInfo(info, placementTypeId, workSetCheckGroupId);
  if (!work_sets.length) {
    showErrorSnackbar("Нет работ в группе для исполнительной схемы");
    return;
  }
  showModal(MODAL_NAMES.ExecListCreateDocument, {
    title: "Исполнительная схема: объёмы работ",
    projectId,
    floorMapId,
    placementTypeId,
    workSetCheckGroupId,
    rows: work_sets.map((s) => ({
      work_set_id: s.work_set_id,
      work_set_name: s.work_set_name,
      unit_name: s.unit_name,
      work_set_amount: s.work_set_amount,
    })),
    documentParams: {},
    confirmContext: "secondary",
    openConfirmModal: (payload) => showModal(MODAL_NAMES.CONFIRM, payload),
    onSuccess: () => refetch(),
  });
}

async function onCompleteClick(row) {
  const code = row.task_type_code;
  if (code === "create_asr") {
    openCreateAsrModal(row);
    return;
  }
  if (code === "create_exec_schema") {
    await openCreateExecSchemaModal(row);
    return;
  }
  if (code === "prorab_k2sv_checked") {
    showModal(MODAL_NAMES.ProrabK2svTaskConfirm, {
      title: "КС2В: согласование прорабом",
      employee_task_id: row.employee_task_id,
      master_url: row.master_url,
      floor_map_document_type_name: row.floor_map_document_type_name,
      document_date: row.document_date,
      file_ext: row.file_ext,
      resident_id: row.resident_id,
      project_type_id: row.project_type_id,
      project_entrance_id: row.project_entrance_id,
      project_id: row.project_id,
      sbs_project_id: row.sbs_project_id,
      onSuccess: () => {
        showSucceedSnackbar();
        refetch();
      },
    });
    return;
  }
  const res = tryOpenEmployeeTaskComplete(row);
  if (!res.ok && res.message) {
    showErrorSnackbar(res.message);
  }
}
</script>

<style scoped lang="scss">
.et-page {
  position: relative;
  min-height: calc(100vh - 72px);
  padding: 8px 24px 20px;
  margin-top: -4px;
  background: #fff;
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  color: #1e293b;
}

.et-page__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 4;
}

.et-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 0;
  padding-bottom: 2px;
}

.et-page__title {
  margin: 0;
  font-size: 20px;
  line-height: 1.25;
  font-weight: 700;
  color: #1e293b;
}

.et-page__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.et-refresh-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  color: #1976d2 !important;
  background: #e3f2fd !important;
  border: 1px solid #bbdefb;
  border-radius: 8px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.et-refresh-btn:hover {
  color: #1565c0 !important;
  background: #dbeafe !important;
  border-color: #90caf9;
}

.et-refresh-btn :deep(.q-icon) {
  font-size: 20px;
}

.et-controls-band {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 -24px 10px;
  padding: 0 24px 4px;
}

.et-filters-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin: 0;
  padding: 6px 10px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: none;
}

.et-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.cd-filter {
  flex: 1;
  min-width: 100px;
}

.cd-filter--md {
  min-width: 168px;
  max-width: 220px;
  flex: 0 1 200px;
}

.cd-filter--search {
  min-width: 240px;
  max-width: 360px;
  flex: 1 1 280px;
}

.et-search-icon {
  color: #64748b;
  font-size: 18px;
}

.et-page .et-filters-row :deep(.cd-filter.q-field--outlined .q-field__control) {
  min-height: 40px;
  height: 40px;
  border-radius: 4px;
  border: none;
  background: #fff;
  box-shadow: none;
}

.et-page .et-filters-row :deep(.cd-filter.q-field--outlined .q-field__control:before) {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
  border-radius: 4px;
}

.et-page .et-filters-row :deep(.cd-filter.q-field--outlined .q-field__control:after) {
  border-radius: 4px;
}

.et-page .et-filters-row :deep(.cd-filter.q-field--focused .q-field__control) {
  border: none;
  box-shadow: none;
}

.et-page .et-filters-row :deep(.cd-filter .q-field__label) {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.et-page .et-filters-row :deep(.cd-filter .q-field__native),
.et-page .et-filters-row :deep(.cd-filter .q-field__input) {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.et-page .et-filters-row :deep(.cd-filter .q-field__native::placeholder),
.et-page .et-filters-row :deep(.cd-filter .q-field__input::placeholder) {
  color: #94a3b8;
}

.et-page .et-filters-row :deep(.cd-filter.q-field--focused .q-field__control:before) {
  border-color: #1976d2 !important;
}

.et-table-card {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  box-shadow: none;
}

.et-table-card :deep(.custom-table .q-table__top) {
  display: none;
}

.et-table-card :deep(.custom-table) {
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.et-table-card :deep(.custom-table thead tr th) {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 0 #e2e8f0;
}

.et-table-card :deep(.custom-table tbody tr td) {
  background: #fff;
  color: #1e293b;
  font-size: 13px;
  border-top: 1px solid #e2e8f0;
  padding: 10px 12px !important;
  vertical-align: middle;
}

.et-table-card :deep(.custom-table tbody tr:hover td) {
  background: #f8fafc;
}

.et-table-card :deep(.custom-table .full-width.row.flex-center) {
  width: 100%;
  min-height: 180px;
  margin: 12px;
  padding: 32px 24px 40px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  color: #94a3b8;
  font-size: 14px;
  text-align: center;
  gap: 8px;
}

.et-table-card :deep(.custom-table .full-width.row.flex-center .q-icon) {
  color: #cbd5e1;
  font-size: 28px !important;
}

.et-table__row--open :deep(td:first-child) {
  box-shadow: inset 3px 0 0 #f59e0b;
}

.et-table__row--done :deep(td:first-child) {
  box-shadow: inset 3px 0 0 #047857;
}

.et-cell__id {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
}

.et-cell__task {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.et-cell__task-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.et-cell__task-code {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.et-cell__main,
.et-cell__secondary {
  display: block;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.et-cell__main {
  color: #1e293b;
  font-weight: 500;
}

.et-cell__secondary {
  color: #64748b;
  font-weight: 400;
}

.et-cell__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.et-cell__meta-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.25;
}

.et-cell__meta-label {
  color: #94a3b8;
  font-weight: 600;
}

.et-cell__tag {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  max-width: 100%;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.et-cell__date {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.et-status {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.et-status__badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid transparent;
  white-space: nowrap;
}

.et-status__badge--done {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}

.et-status__badge--open {
  background: #fff8e1;
  border-color: #fde68a;
  color: #92400e;
}

.et-action-btn {
  height: 28px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  box-shadow: none;
}

.et-action-done {
  color: #94a3b8;
  font-size: 14px;
}

@media (max-width: 920px) {
  .et-page {
    padding: 14px;
  }

  .et-controls-band {
    margin-left: -14px;
    margin-right: -14px;
    padding-left: 14px;
    padding-right: 14px;
  }

  .cd-filter--md,
  .cd-filter--search {
    min-width: 0;
    max-width: none;
    flex: 1 1 100%;
  }
}

@media (max-width: 640px) {
  .et-page__title {
    font-size: 18px;
  }

  .et-table-card :deep(.custom-table thead tr th),
  .et-table-card :deep(.custom-table tbody tr td) {
    font-size: 12px;
    padding: 8px !important;
  }

  .et-action-btn {
    height: 28px;
    padding: 0 10px;
    font-size: 11px;
  }
}
</style>
